import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Briefcase, ChevronUp, ChevronDown, UserCircle } from 'lucide-react';
import { LoanOfficerWithContext, Loan } from '../types';
import { formatCurrency, formatNumber } from '../utils/formatters';
interface LoanOfficerDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  loanOfficer: LoanOfficerWithContext | null;
}
type LoanSortField = 'borrower' | 'product' | 'closeDate' | 'amount' | 'revenue';
type SortDir = 'asc' | 'desc';
function SortHeader({
  label,
  field,
  activeField,
  direction,
  onSort,
  align = 'left'
}: {
  label: string;
  field: string;
  activeField: string;
  direction: SortDir;
  onSort: (field: any) => void;
  align?: 'left' | 'right';
}) {
  const isActive = field === activeField;
  return <button onClick={e => {
    e.stopPropagation();
    onSort(field);
  }} className={`group inline-flex items-center gap-1 text-xs uppercase ${isActive ? 'text-slate-200' : 'text-slate-500 hover:text-slate-300'} transition-colors ${align === 'right' ? 'justify-end w-full' : ''}`} data-id="element-367">
      <span data-id="element-368">{label}</span>
      {isActive ? direction === 'asc' ? <ChevronUp className="w-3 h-3 text-indigo-400" data-id="element-369" /> : <ChevronDown className="w-3 h-3 text-indigo-400" data-id="element-370" /> : <ChevronDown className="w-3 h-3 opacity-0 group-hover:opacity-40 transition-opacity" data-id="element-371" />}
    </button>;
}
function compare(a: any, b: any, dir: SortDir) {
  if (typeof a === 'string' && typeof b === 'string') {
    return dir === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
  }
  return dir === 'asc' ? a - b : b - a;
}
export function LoanOfficerDetailModal({
  isOpen,
  onClose,
  loanOfficer
}: LoanOfficerDetailModalProps) {
  const [sortField, setSortField] = useState<LoanSortField>('revenue');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const sorted = useMemo<Loan[]>(() => {
    if (!loanOfficer) return [];
    return [...loanOfficer.loans].sort((a, b) => compare(a[sortField], b[sortField], sortDir));
  }, [loanOfficer, sortField, sortDir]);
  const handleSort = (field: LoanSortField) => {
    if (field === sortField) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');else {
      setSortField(field);
      setSortDir('desc');
    }
  };
  if (!loanOfficer) return null;
  return <AnimatePresence data-id="element-372">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" onClick={onClose} data-id="element-373" />
          <div className="fixed inset-0 flex items-start justify-center z-50 p-4 pointer-events-none overflow-y-auto" data-id="element-374">
            <motion.div initial={{
          opacity: 0,
          scale: 0.97,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.97,
          y: 20
        }} className="bg-[#141a2c] rounded-3xl my-6 max-w-4xl w-full shadow-2xl pointer-events-auto border border-white/10 relative overflow-hidden" data-id="element-375">
              {/* Header */}
              <div className="flex items-start justify-between p-6 border-b border-white/5" data-id="element-376">
                <div className="flex items-center gap-4" data-id="element-377">
                  <div className="w-16 h-16 rounded-2xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center text-violet-200 font-bold text-xl" data-id="element-378">
                    {loanOfficer.initials}
                  </div>
                  <div data-id="element-379">
                    <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-300 text-[10px] uppercase tracking-wider font-medium mb-1.5" data-id="element-380">
                      <UserCircle className="w-3 h-3" data-id="element-381" /> Loan Officer
                    </div>
                    <h2 className="text-2xl font-bold text-white" data-id="element-382">
                      {loanOfficer.name}
                    </h2>
                    <p className="text-sm text-slate-400" data-id="element-383">
                      Director: {loanOfficer.directorName} · AVP:{' '}
                      {loanOfficer.avpName} · {loanOfficer.division} Division
                    </p>
                  </div>
                </div>
                <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1" data-id="element-384">
                  <X className="w-5 h-5" data-id="element-385" />
                </button>
              </div>

              {/* Summary KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-6 border-b border-white/5" data-id="element-386">
                <div className="bg-black/30 rounded-xl p-4 border border-white/5" data-id="element-387">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-388">Loans Closed</p>
                  <p className="text-lg font-bold text-white" data-id="element-389">
                    {formatNumber(loanOfficer.loansClosed)}
                  </p>
                </div>
                <div className="bg-black/30 rounded-xl p-4 border border-white/5" data-id="element-390">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-391">Loan Volume</p>
                  <p className="text-lg font-bold text-white" data-id="element-392">
                    {formatCurrency(loanOfficer.loanVolume, true)}
                  </p>
                </div>
                <div className="bg-black/30 rounded-xl p-4 border border-white/5" data-id="element-393">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-394">Total Revenue</p>
                  <p className="text-lg font-bold text-white" data-id="element-395">
                    {formatCurrency(loanOfficer.totalRevenue)}
                  </p>
                </div>
                <div className="bg-black/30 rounded-xl p-4 border border-white/5" data-id="element-396">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-397">Avg Revenue</p>
                  <p className="text-lg font-bold text-emerald-400" data-id="element-398">
                    {formatCurrency(loanOfficer.avgRevenue)}
                  </p>
                </div>
              </div>

              {/* Loans */}
              <div className="p-6 max-h-[60vh] overflow-y-auto" data-id="element-399">
                <div className="flex items-center gap-2 text-xs uppercase text-slate-500 mb-3" data-id="element-400">
                  <Briefcase className="w-3.5 h-3.5" data-id="element-401" /> Contributing Loans
                </div>
                <div className="rounded-lg overflow-hidden bg-black/30 border border-white/5" data-id="element-402">
                  <table className="w-full text-left" data-id="element-403">
                    <thead data-id="element-404">
                      <tr className="border-b border-white/5" data-id="element-405">
                        <th className="py-2 px-3" data-id="element-406">
                          <SortHeader label="Borrower" field="borrower" activeField={sortField} direction={sortDir} onSort={handleSort} data-id="element-407" />
                        </th>
                        <th className="py-2 px-3" data-id="element-408">
                          <SortHeader label="Product" field="product" activeField={sortField} direction={sortDir} onSort={handleSort} data-id="element-409" />
                        </th>
                        <th className="py-2 px-3" data-id="element-410">
                          <SortHeader label="Date" field="closeDate" activeField={sortField} direction={sortDir} onSort={handleSort} data-id="element-411" />
                        </th>
                        <th className="py-2 px-3 text-right" data-id="element-412">
                          <SortHeader label="Amount" field="amount" activeField={sortField} direction={sortDir} onSort={handleSort} align="right" data-id="element-413" />
                        </th>
                        <th className="py-2 px-3 text-right" data-id="element-414">
                          <SortHeader label="Revenue" field="revenue" activeField={sortField} direction={sortDir} onSort={handleSort} align="right" data-id="element-415" />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5" data-id="element-416">
                      {sorted.map(loan => <tr key={loan.id} data-id="element-417">
                          <td className="py-2 px-3 text-sm text-white" data-id="element-418">
                            {loan.borrower}
                          </td>
                          <td className="py-2 px-3 text-xs text-slate-400" data-id="element-419">
                            {loan.product}
                          </td>
                          <td className="py-2 px-3 text-xs text-slate-400" data-id="element-420">
                            {loan.closeDate}
                          </td>
                          <td className="py-2 px-3 text-sm text-white text-right" data-id="element-421">
                            {formatCurrency(loan.amount)}
                          </td>
                          <td className="py-2 px-3 text-sm text-emerald-400 text-right" data-id="element-422">
                            {formatCurrency(loan.revenue)}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-white/5 flex items-center justify-end bg-black/10" data-id="element-423">
                <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-sm font-medium transition-colors border border-white/10" data-id="element-424">
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}